import React, { memo, useMemo, useCallback, useEffect } from 'react';
import { Box } from '@apia/theme';
import Config from 'config';
import { useDebounceFn, useUpdateEffect } from 'ahooks';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/hooks';
import { queryActions } from '../../../../../store/querySlice';
import { ConfirmModal } from '../../../../common';
import ApiaFinderNavbar from './modalComponents/ApiaFinderNavbar';
import AppliedFiltersLabel from './modalComponents/AppliedFiltersLabel';
import {
  TResponsiveTableRow,
  TResponsiveTableRowsSelectionEvent,
  TResponsiveTableSortChangeEvent,
} from '../../../../common/generic/responsiveTable/types';
import { useFieldContext } from '../../fieldsStore/FieldContext';
import ApiaComponentContext, {
  ApiaComponentContextPrimitive,
} from '../../apiaComponent/ApiaComponentContext';
import { TApiaFilter } from '../../../../../types';
import useContext from '../../../../../hooks/useContext';
import ResponsiveTableContext from '../../../../common/generic/responsiveTable/ResponsiveTableContext';
import ResponsiveTable from '../../../../common/generic/responsiveTable';
import { responsiveTableActions } from '../../../../../store/responsiveTableSlice';
import store from '../../../../../store';
import parameters from '../../../../../config/parameters';

const ApiaFinderModal = memo(() => {
  const { fireEvent } = useFieldContext();
  const context = useContext(ApiaComponentContextPrimitive);
  const { fieldId } = context;

  const dispatch = useAppDispatch();

  const {
    columns,
    filters,
    filtersConfig,
    loading,
    modalData,
    pageInfo,
    rows,
    // selectedRows,
    shouldShowModal,
  } = useAppSelector((globalState) => globalState.querySlice.queries[fieldId]);
  const selectedRows = useAppSelector(
    (global) =>
      global.responsiveTableSlice[fieldId]?.selectedRows.map((rowIndex) => {
        return store.getState().responsiveTableSlice[fieldId].rows[rowIndex];
      }) ?? [],
  );
  useUpdateEffect(() => {
    dispatch(responsiveTableActions.update({ tableName: fieldId, rows }));
  }, [rows]);
  useUpdateEffect(() => {
    dispatch(responsiveTableActions.update({ tableName: fieldId, columns }));
  }, [columns]);
  useUpdateEffect(() => {
    dispatch(
      responsiveTableActions.update({
        tableName: fieldId,
        filters: filters?.map((current) => current.apiaFilter),
      }),
    );
  }, [filters]);

  const modalRef = React.useRef<HTMLDivElement>(null);

  const memoizedFilters = useMemo(
    () => filters?.map((currentFilter) => currentFilter.apiaFilter) ?? [],
    [filters],
  );

  const handleSortChange = useCallback(
    (sortValue: TResponsiveTableSortChangeEvent) => {
      void dispatch(
        queryActions.sort({ fieldId, sortValue, ignoreSetFilters: true }),
      );
    },
    [dispatch, fieldId],
  );

  const handleRowSelection = useCallback(
    (
      newSelectedRows:
        | TResponsiveTableRow[]
        | TResponsiveTableRowsSelectionEvent,
    ) => {
      if (newSelectedRows.length === 0) return;

      function isEvent(
        evaluate: TResponsiveTableRow[] | TResponsiveTableRowsSelectionEvent,
      ): evaluate is TResponsiveTableRowsSelectionEvent {
        return 'index' in evaluate[0];
      }
      const row = isEvent(newSelectedRows)
        ? newSelectedRows[0].row
        : newSelectedRows[0];

      if (row.id) {
        dispatch(
          queryActions.setState({
            dispatchRow: row,
            shouldShowModal: false,
            loading: 0,
            fieldId,
          }),
        );
        fireEvent('onModalReturn');
      }
    },
    [dispatch, fieldId, fireEvent],
  );

  const { run: runFiltersAutomatically } = useDebounceFn(
    React.useCallback(
      (filter: TApiaFilter) => {
        void dispatch(
          queryActions.updateFilters({
            fieldId,
            filters: [{ ...filter, changeFiltersTimestamp: Date.now() }],
            forceSearch: filtersConfig?.runFiltersAutomatically ?? false,
          }),
        );
      },
      [dispatch, fieldId, filtersConfig?.runFiltersAutomatically],
    ),
    { wait: parameters.timing.keyboardFilterDebounceTime },
  );

  const [isOpen, setIsOpen] = React.useState(false);
  useEffect(() => {
    const load = async () => {
      await dispatch(queryActions.refresh({ fieldId }));
      setIsOpen(true);
    };

    if (shouldShowModal) void load();
    else {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowModal]);

  const handleFilterBlur = React.useCallback(
    (filter: TApiaFilter) => {
      if (!filter.options && !filter.detectOnChange) {
        void dispatch(
          queryActions.updateFilters({
            fieldId,
            filters: [{ ...filter, changeFiltersTimestamp: Date.now() }],
            forceSearch: false,
          }),
        );
      }
    },
    [dispatch, fieldId],
  );

  const handleFilterChange = React.useCallback(
    (filter: TApiaFilter) => {
      if (
        filter.options ||
        filtersConfig?.runFiltersAutomatically ||
        filter.detectOnChange ||
        filter.type === 'D' ||
        filter.type === 'date'
      ) {
        if (filtersConfig?.runFiltersAutomatically) {
          runFiltersAutomatically(filter);
        } else
          void dispatch(
            queryActions.updateFilters({
              fieldId,
              filters: [{ ...filter, changeFiltersTimestamp: Date.now() }],
              forceSearch: false,
            }),
          );
      }
    },
    [
      dispatch,
      fieldId,
      filtersConfig?.runFiltersAutomatically,
      runFiltersAutomatically,
    ],
  );

  const handleFilterPressEnter = React.useCallback(
    (filter: TApiaFilter) => {
      void dispatch(
        queryActions.updateFilters({
          fieldId,
          filters: [{ ...filter, changeFiltersTimestamp: Date.now() }],
          forceSearch: false,
        }),
      );
    },
    [dispatch, fieldId],
  );

  const memoizedCustomLabels = React.useMemo(
    () =>
      modalData?.noExecFirstTime && pageInfo?.hasMore !== false
        ? { noRegisters: '' }
        : undefined,
    [modalData?.noExecFirstTime, pageInfo?.hasMore],
  );

  const sx = React.useMemo(
    () => ({ variant: 'layout.common.modals.apiaFinder' }),
    [],
  );
  const confirmProps = React.useMemo(
    () => ({
      disabled: selectedRows === undefined,
    }),
    [selectedRows],
  );
  const initialFocusGetter = React.useCallback(
    (modal: HTMLElement) =>
      modal?.querySelector('[tabIndex="0"]') as HTMLElement,
    [],
  );
  const onCancel = React.useCallback(() => {
    dispatch(
      queryActions.setState({
        fieldId,
        shouldShowModal: false,
      }),
    );
  }, [dispatch, fieldId]);
  const onConfirm = React.useCallback(() => {
    if (selectedRows && selectedRows.length > 0)
      void handleRowSelection(selectedRows);
    else onCancel();
  }, [handleRowSelection, onCancel, selectedRows]);
  const NavBar = React.useMemo(() => <ApiaFinderNavbar />, []);

  if (!columns) return null;

  return (
    <ApiaComponentContext value={context}>
      <ResponsiveTableContext
        isMultiple={false}
        columns={columns}
        filters={memoizedFilters}
        rows={rows}
        label={modalData?.title ?? ''}
        isLoading={!!loading}
        onFilterBlur={handleFilterBlur}
        onFilterChange={handleFilterChange}
        onFilterPressEnter={handleFilterPressEnter}
        onSelectRows={handleRowSelection}
        onSortChange={handleSortChange}
        customLabels={memoizedCustomLabels}
        name={fieldId}
      >
        <ConfirmModal
          className="apiaFinderModal"
          confirmProps={confirmProps}
          initialFocusGetter={initialFocusGetter}
          isOpen={isOpen}
          NavBar={NavBar}
          onCancel={onCancel}
          onConfirm={onConfirm}
          ref={modalRef}
          shouldCloseOnEsc={!loading}
          shouldCloseOnOverlayClick={!loading}
          size="finder"
          stretch
          sx={sx}
          data-variant="layout.common.modals.apiaFinder"
          title={modalData?.title}
        >
          <Box className="finder__content">
            <ResponsiveTable />
          </Box>
          <Box className="finder__statistics">
            <AppliedFiltersLabel />
            {!modalData?.hideCount && (
              <Box className="results">
                {rows &&
                  `${Config.FINDER_RESULT} ${pageInfo?.totalRecords ?? ''}
                ${Config.FINDER_FOUND_RESULTS} ${
                  pageInfo?.hasMore ? Config.FINDER_MODAL_EXISTS_MORE_DATA : ''
                }`}
              </Box>
            )}
          </Box>
        </ConfirmModal>
      </ResponsiveTableContext>
    </ApiaComponentContext>
  );
});

ApiaFinderModal.displayName = 'ApiaFinderModal';

export default ApiaFinderModal;
